'use client'

import { Mail, Phone, Globe } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { SITE_CONFIG, COMPANY_INFO } from '@/lib/constants'
import { flagshipAccentAt, FLAGSHIP_ACCENT_VAR } from '@/lib/flagship-colors'
import { LegalPage, GlassSection, SubSection, InfoBox } from './legal/LegalPage'

const accent = (i: number) => FLAGSHIP_ACCENT_VAR[flagshipAccentAt(i)]

export default function ImpressumPageClient() {
  const t = useTranslations('legal.impressum')
  const liabilityContentParagraphs = t.raw('liabilityContentParagraphs') as string[]
  const reportingParagraphs = t.raw('reportingParagraphs') as string[]
  const liabilityLinksParagraphs = t.raw('liabilityLinksParagraphs') as string[]
  const hostingParagraphs = t.raw('hostingParagraphs') as string[]
  const copyrightParagraphs = t.raw('copyrightParagraphs') as string[]

  return (
    <LegalPage title={t('title')} subtitle={t('subtitle')}>
      {/* Provider details */}
      <GlassSection title={t('companyInfoHeading')} accent={accent(0)}>
        <div className="grid gap-4 sm:grid-cols-2">
          <InfoBox>
            <p className="font-semibold text-[var(--flagship-text)]">{COMPANY_INFO.legalName}</p>
            <p>{COMPANY_INFO.ownerName}</p>
            <p>{COMPANY_INFO.address.street}</p>
            <p>
              {COMPANY_INFO.address.postalCode} {COMPANY_INFO.address.city}
            </p>
            <p>{COMPANY_INFO.address.country}</p>
          </InfoBox>
          <InfoBox>
            <p className="mb-2 font-semibold text-[var(--flagship-text)]">{t('contactHeading')}</p>
            <p className="flex items-center gap-2">
              <Phone className="h-4 w-4 shrink-0 text-[var(--flagship-cyan)]" />
              <a href={`tel:${SITE_CONFIG.phone}`}>{SITE_CONFIG.phone}</a>
            </p>
            <p className="flex items-center gap-2">
              <Mail className="h-4 w-4 shrink-0 text-[var(--flagship-cyan)]" />
              <a href={`mailto:${SITE_CONFIG.email}`}>{SITE_CONFIG.email}</a>
            </p>
            <p className="flex items-center gap-2">
              <Globe className="h-4 w-4 shrink-0 text-[var(--flagship-cyan)]" />
              <a href={SITE_CONFIG.url} className="break-all">
                {SITE_CONFIG.url}
              </a>
            </p>
          </InfoBox>
        </div>
        <SubSection title={t('vatIdHeading')}>
          <p>
            {t('vatIdText')} <strong>{COMPANY_INFO.vatId}</strong>
          </p>
        </SubSection>
      </GlassSection>

      {/* Liability */}
      <GlassSection title={t('liabilityContentHeading')} accent={accent(1)}>
        {liabilityContentParagraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        <SubSection title={t('reportingHeading')}>
          {reportingParagraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </SubSection>
      </GlassSection>

      <GlassSection title={t('liabilityLinksHeading')} accent={accent(2)}>
        {liabilityLinksParagraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </GlassSection>

      {/* Digital Services transparency */}
      <GlassSection title={t('transparencyHeading')} accent={accent(3)}>
        <p>{t('transparencyIntro')}</p>
        <ul className="list-inside list-disc space-y-1">
          <li>
            <strong>{t('transparencyServiceTypeLabel')}</strong> {t('transparencyServiceTypeValue')}
          </li>
          <li>
            <strong>{t('transparencyAreaLabel')}</strong> {COMPANY_INFO.serviceAreas.join(', ')}
          </li>
          <li>
            <strong>{t('transparencyReportingLabel')}</strong> {t('transparencyReportingValue')}
          </li>
          <li>
            <strong>{t('transparencyLanguageLabel')}</strong> {COMPANY_INFO.languages.join(', ')}
          </li>
        </ul>
      </GlassSection>

      <GlassSection title={t('hostingHeading')} accent={accent(4)}>
        {hostingParagraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </GlassSection>

      <GlassSection title={t('copyrightHeading')} accent={accent(5)}>
        {copyrightParagraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </GlassSection>

      {/* Consumer dispute resolution (§ 36 VSBG). The EU ODR platform notice was dropped: the
          platform closed on 20 July 2025 (Regulation (EU) 2024/3228). */}
      <GlassSection title={t('consumerDisputeHeading')} accent={accent(6)}>
        <p>{t('consumerDisputeText')}</p>
      </GlassSection>
    </LegalPage>
  )
}
